/**
 * EventFeatureToggles - Admin component to enable/disable event modules
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, Utensils, Home, Coins, FileText, Award, 
  ToggleLeft, ToggleRight, Loader2, AlertTriangle, Shield, MapPin, Wifi
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EventFeatureToggles = ({ eventId }) => {
  const queryClient = useQueryClient();

  // Fetch current feature settings
  const { data: features, isLoading, error } = useQuery({
    queryKey: ['event-features', eventId],
    queryFn: async () => {
      const response = await api.get(`/admin/events/${eventId}/features`);
      return response.data;
    },
    enabled: !!eventId
  });

  // Toggle networking mutation
  const toggleNetworkingMutation = useMutation({
    mutationFn: async (enabled) => {
      const response = await api.put(`/admin/events/${eventId}/features/networking`, { enabled });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-features', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      toast.success(data.message || `Networking ${data.networking_enabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update networking setting');
    }
  });

  // Toggle province mutation
  const toggleProvinceMutation = useMutation({
    mutationFn: async (enabled) => {
      const response = await api.put(`/admin/events/${eventId}/features/province`, { enabled });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-features', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      toast.success(data.message || `Province hierarchy ${data.province_enabled ? 'enabled' : 'disabled'}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update province setting');
    }
  });

  // Generic feature mutation factory (iter 312)
  const _makeFeatureMutation = (featureKey) => useMutation({
    mutationFn: async (enabled) => {
      const response = await api.put(`/admin/events/${eventId}/features/${featureKey}`, { enabled });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-features', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      toast.success(data.message || `${featureKey} updated`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || `Failed to update ${featureKey}`);
    }
  });

  const toggleMealsMutation = _makeFeatureMutation('meals');
  const toggleHousingMutation = _makeFeatureMutation('housing');
  const toggleEconomyMutation = _makeFeatureMutation('economy');
  const toggleWifiMutation = _makeFeatureMutation('wifi');

  const wifi = features?.wifi;

  const setCampusMutation = useMutation({
    mutationFn: async (tenantId) => {
      const response = await api.put(`/admin/events/${eventId}/wifi/provider`,
        { provider_tenant_id: tenantId || null });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-features', eventId] });
      toast.success('Wi-Fi campus updated');
    },
    onError: (error) => toast.error(error.response?.data?.detail || 'Failed to update campus'),
  });

  // Feature definitions with metadata
  const featureConfig = [
    {
      key: 'networking_enabled',
      label: 'Networking',
      description: 'Allow delegates to connect via QR codes and request meetings',
      icon: Users,
      color: 'text-blue-500',
      canToggle: true,
      toggleMutation: toggleNetworkingMutation,
      warning: 'Disabling will hide networking from all delegate menus and block related APIs'
    },
    {
      key: 'meals_enabled',
      label: 'Meal Management',
      description: 'Track meal selections, dietary requirements, and meal scanning',
      icon: Utensils,
      color: 'text-orange-500',
      canToggle: true,
      toggleMutation: toggleMealsMutation,
      warning: 'Disabling will hide meal management from delegate menus and block related APIs'
    },
    {
      key: 'housing_enabled',
      label: 'Housing/Accommodation',
      description: 'Manage room assignments, check-in/out, and housing requests',
      icon: Home,
      color: 'text-green-500',
      canToggle: true,
      toggleMutation: toggleHousingMutation,
      warning: 'Disabling will hide housing from delegate menus and block related APIs'
    },
    {
      key: 'economy_enabled',
      label: 'Event Economy',
      description: 'Digital wallet, payments, and token-based transactions',
      icon: Coins,
      color: 'text-yellow-500',
      canToggle: true,
      toggleMutation: toggleEconomyMutation,
      warning: 'Disabling will hide economy/wallet from delegate menus and block related APIs'
    },
    {
      key: 'surveys_enabled',
      label: 'Surveys & Feedback',
      description: 'Post-session and event surveys for attendee feedback',
      icon: FileText,
      color: 'text-purple-500',
      canToggle: false,
    },
    {
      key: 'certificates_enabled',
      label: 'Certificates',
      description: 'Generate and distribute attendance certificates',
      icon: Award,
      color: 'text-indigo-500',
      canToggle: false,
    },
    {
      key: 'province_enabled',
      label: 'Province / District / Assembly',
      description: 'Show province, district, and assembly hierarchy in member registration',
      icon: MapPin,
      color: 'text-teal-500',
      canToggle: true,
      toggleMutation: toggleProvinceMutation,
      warning: 'Disabling will hide province/district/assembly fields from the registration form'
    },
    // Guest Wi-Fi only appears when a campus provider exists to honour it.
    ...(wifi?.available ? [{
      key: 'wifi_enabled',
      label: 'Guest Wi-Fi',
      description: `Inherits the campus provider (${wifi.provider}${
        wifi.source === 'override' ? ' · per-event override' : ' · inherited'}). `
        + 'Delegates get vouchers and captive-portal access for this event.',
      icon: Wifi,
      color: 'text-sky-500',
      canToggle: true,
      defaultEnabled: false,
      toggleMutation: toggleWifiMutation,
      warning: 'Disabling stops new voucher redemptions for this event'
    }] : [])
  ];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load feature settings. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const currentFeatures = features?.features || {};

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Feature Toggles
          </CardTitle>
          <CardDescription>
            Enable or disable modules for this event. Disabled modules are hidden from delegates
            but data is preserved for future re-enablement.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {featureConfig.map((feature, index) => {
            const isEnabled = feature.defaultEnabled === false
              ? currentFeatures[feature.key] === true
              : currentFeatures[feature.key] !== false;
            const Icon = feature.icon;
            const isToggling = feature.toggleMutation?.isPending;

            return (
              <React.Fragment key={feature.key}>
                {index > 0 && <Separator />}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg bg-muted ${feature.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-base font-medium">{feature.label}</Label>
                        <Badge variant={isEnabled ? 'default' : 'secondary'}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                      {!isEnabled && feature.warning && (
                        <p className="text-xs text-amber-600 mt-1">
                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                          {feature.warning}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {feature.canToggle ? (
                      <div className="flex items-center gap-2">
                        {isToggling && <Loader2 className="h-4 w-4 animate-spin" />}
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => feature.toggleMutation.mutate(checked)}
                          disabled={isToggling}
                          data-testid={`toggle-${feature.key}`}
                        />
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        Toggle via Event Settings
                      </Badge>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </CardContent>
      </Card>

      {/* Campus Wi-Fi source — override which configured campus this event uses */}
      {wifi?.available && (
        <Card data-testid="event-wifi-campus-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wifi className="h-4 w-4 text-sky-500" /> Wi-Fi campus
            </CardTitle>
            <CardDescription>
              Provider <strong>{wifi.provider}</strong> · {wifi.source === 'override'
                ? 'chosen for this event' : `inherited (${wifi.source})`}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3">
            <Select
              value={wifi.source === 'override' ? (wifi.provider_tenant_id || '') : 'inherit'}
              onValueChange={(v) => setCampusMutation.mutate(v === 'inherit' ? null : v)}
              disabled={setCampusMutation.isPending}>
              <SelectTrigger className="w-full max-w-[320px]" data-testid="event-wifi-campus-select">
                <SelectValue placeholder="Inherit campus provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Inherit campus provider (default)</SelectItem>
                {(wifi.campuses || []).filter((c) => c.tenant_id).map((c) => (
                  <SelectItem key={c.tenant_id} value={c.tenant_id}>
                    {c.provider} · {c.tenant_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {setCampusMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          </CardContent>
        </Card>
      )}

      {/* Networking-specific info when disabled */}
      {currentFeatures.networking_enabled === false && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Networking is currently disabled.</strong>
            <ul className="mt-2 ml-4 list-disc text-sm space-y-1">
              <li>Networking tabs are hidden from delegate portal</li>
              <li>QR connection requests are blocked</li>
              <li>Meeting requests are disabled</li>
              <li>Existing connections are preserved and will be accessible when re-enabled</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default EventFeatureToggles;
